import { FormEvent, SyntheticEvent, useCallback, useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { prepareErrorsFromYupValidationError } from 'src/utils/helpers/errors';
import { AppContext } from 'src/context';
import {
    useCreateSubLocationMutation,
    TCreateSubLocationPayload,
} from 'src/hooks/queries/subLocations/useCreateSubLocationMutation';
import {
    useUpdateSubLocationMutation,
    TUpdateSubLocationPayload,
} from 'src/hooks/queries/subLocations/useUpdateSubLocationMutation';
import { DateTime } from 'luxon';
import { useAuth } from 'src/utils/auth';
import get from 'lodash/get';
import { DATE_FORMAT, DEFAULT_TIME_ZONE } from 'src/constants';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { usePrevious } from 'src/hooks/usePrevious';
import type { TAddSubLocationForm, TCreateEditSubLocationModalProps } from '../types';

export const initFormData: TAddSubLocationForm = {
    name: '',
    description: '',
    capacity: 0,
    flex: 0,
};

export const initFormErrors: Partial<Record<keyof TAddSubLocationForm, string[]>> = {
    name: [],
    capacity: [],
    flex: [],
};

export const useCreateEditSubLocationState = (props: TCreateEditSubLocationModalProps) => {
    const { isEdit = false, locationId, id, refetchCapacities, isOffice = true } = props;
    const {
        actions: { handleCloseModal },
    } = useContext(AppContext);
    const { user: userData } = useAuth();
    const user = userData?.profile.user;
    const timeZone = get(user, 'timezone', DEFAULT_TIME_ZONE);
    const [formData, setFormState] = useState<TAddSubLocationForm>(initFormData);
    const prevFormData = usePrevious(formData);
    const [errors, setErrors] =
        useState<Partial<Record<keyof TAddSubLocationForm, string[]>>>(initFormErrors);
    const { mutateAsync: createSubLocation, isLoading: createSubLocationIsLoading } =
        useCreateSubLocationMutation({ locationId });
    const { mutateAsync: updateSubLocation, isLoading: updateSubLocationIsLoading } =
        useUpdateSubLocationMutation({ locationId });

    useEffect(() => {
        if (isEdit && isEqual(formData, initFormData) && !prevFormData) {
            setFormState((prevState) => ({
                ...prevState,
                ...pick(props, ['name', 'description', 'capacity', 'flex']),
            }));
        }
    }, [formData, isEdit, prevFormData, props]);

    const validationSchema = yup.object().shape({
        name: yup.string().required('Name is required').typeError('Name is required'),
        capacity: yup
            .number()
            .min(0)
            .required()
            .typeError('Capacity must be a positive number or zero'),
        flex: yup.number().min(0).required().typeError('Flex must be a positive number or zero'),
    });
    const homeValidationSchema = yup.object().shape({
        name: yup.string().required('Name is required').typeError('Name is required'),
        capacity: yup.number().nullable().notRequired(),
        flex: yup.number().nullable().notRequired(),
    });

    const handleChange = (event: FormEvent<HTMLInputElement>) => {
        const { name } = event.currentTarget;
        const value: unknown = event.currentTarget?.value;
        setFormState((prevState) => ({ ...prevState, [name]: value }));
        setErrors((prevState) => ({ ...prevState, [name]: [] }));
    };

    // validate function
    const handleValidate = useCallback(async () => {
        try {
            if (isEdit && !isOffice) {
                await homeValidationSchema.validate(formData, {
                    abortEarly: false,
                    recursive: true,
                });
            } else {
                await validationSchema.validate(formData, { abortEarly: false, recursive: true });
            }
            return true;
        } catch (err) {
            const result = prepareErrorsFromYupValidationError<TAddSubLocationForm>(
                err as unknown as yup.ValidationError
            );
            setErrors(result);
            return false;
        }
    }, [isEdit, isOffice, validationSchema, formData, homeValidationSchema]);

    const handleSubmit = useCallback(
        async (event: SyntheticEvent) => {
            event.preventDefault();
            const isValid = await handleValidate();

            if (!isValid || !user) {
                return;
            }

            if (!isEdit) {
                try {
                    const payload: TCreateSubLocationPayload = {
                        name: formData.name,
                        description: formData.description,
                        location_id: isOffice ? locationId : null,
                        capacity: {
                            capacity:
                                typeof formData.capacity === 'string'
                                    ? parseInt(formData.capacity, 10)
                                    : formData.capacity ?? 0,
                            flex:
                                typeof formData.flex === 'string'
                                    ? parseInt(formData.flex, 10)
                                    : formData.flex ?? 0,
                            effective_date: DateTime.local()
                                .setZone(timeZone)
                                .toFormat(DATE_FORMAT),
                        },
                    };
                    await createSubLocation(payload);
                } catch (error) {
                    return;
                }
            }

            if (isEdit && id) {
                try {
                    const payload: TUpdateSubLocationPayload = {
                        id,
                        name: formData.name,
                        description: formData.description,
                        location_id: isOffice ? locationId : null,
                        capacity: {
                            capacity:
                                typeof formData.capacity === 'string'
                                    ? parseInt(formData.capacity, 10)
                                    : formData.capacity,
                            flex:
                                typeof formData.flex === 'string'
                                    ? parseInt(formData.flex, 10)
                                    : formData.flex,
                            effective_date: DateTime.local()
                                .setZone(timeZone)
                                .toFormat(DATE_FORMAT),
                        },
                    };

                    await updateSubLocation(payload);

                    if (typeof refetchCapacities === 'function') {
                        refetchCapacities();
                    }
                } catch (error) {
                    return;
                }
            }

            handleCloseModal();
        },
        [
            formData,
            id,
            isEdit,
            isOffice,
            locationId,
            user,
            timeZone,
            handleCloseModal,
            handleValidate,
            refetchCapacities,
            updateSubLocation,
            createSubLocation,
        ]
    );

    return {
        formData,
        handleChange,
        errors,
        handleSubmit,
        handleCloseModal,
        isLoading: createSubLocationIsLoading || updateSubLocationIsLoading,
    };
};
